import React from 'react'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'
import { ButtonGroup, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import { getColumnValue } from './knockHelpers'
import DeleteModalContainer, { DeleteResponseToMessageMapper } from '@cbgms/components/form/Dialogs/DeleteModalContainer'
import { IPaginatedRequest } from '@cbgms/api/request'
import { IPaginatedResponse } from '@cbgms/api/response'
import { Alert } from '@carsys/ui/src/Alert'
import CloseIcon from '@mui/icons-material/Close'
import { Loader } from '@carsys/ui'

interface NoResultMessageProps {
  message: string
}

const NoResultMessage: React.FC<NoResultMessageProps> = props => {
  const { t } = useTranslation()
  return <Alert color='muted'>{t(props.message)} </Alert>
}

interface HeaderCellProps {
  field: string
}

const HeaderCell: React.FC<HeaderCellProps> = props => {
  const { t } = useTranslation()
  return <TableCell variant='head'>{t(`entities.${props.field.toLowerCase()}`)}</TableCell>
}

interface DeleteButtonProps {
  onClick: () => void
}

const DeleteButton: React.FC<DeleteButtonProps> = props => {
  const { t } = useTranslation()

  return (
    <Tooltip title={`${t('global.delete')}`} placement='top' arrow>
      <IconButton onClick={props.onClick}>
        <CloseIcon />
      </IconButton>
    </Tooltip>
  )
}

interface DefaultProps {
  fields: (string | FieldWithOptionalCustomRender)[]
  noResultsMessage: string
  orderBy: string
  perPage: number
  query: string
}

export interface FieldWithOptionalCustomRenderProps {
  content: any
}

interface FieldWithOptionalCustomRender {
  key: string
  Component?: React.ComponentType<FieldWithOptionalCustomRenderProps>
}

export interface DeleteOptionsProps {
  key: string
  text: string
  value: string
}

interface OwnProps<T> {
  ref?: React.RefObject<any>
  action: (params: IPaginatedRequest) => any
  button?: (row: T) => React.ReactElement
  queryValue: string
  apiFields?: string[]
  deleteOptions?: DeleteOptions<T>
  /** Widget refresh interval in milliseconds. */
  refreshInterval?: number
  isPortal?: boolean
  scrollable?: boolean
  scrollHeight?: number
}

interface DeleteOptions<T> {
  deleteReasons?: DeleteOptionsProps[]
  mapApiResponseToMessage: DeleteResponseToMessageMapper
  onDelete: (row: T, dryRun: boolean, reason?: string, props?: Props<T>) => Promise<any>
  skipDryRun?: boolean
  translationKey: string
}

const mapDispatchToProps = (dispatch: Dispatch, props: OwnProps<any>) => ({
  action: (params: IPaginatedRequest) => dispatch(props.action(params))
})

const enhance = connect(null, mapDispatchToProps, null, { forwardRef: true })

interface Props<T> extends DefaultProps, OwnProps<T> {}

interface State<T> {
  data: T[]
  isLoading: boolean
  types: any
  selectedRow: T | null
  modal: boolean
}

export class KnockWidget<T = any> extends React.PureComponent<Props<T>, State<T>> {
  private timerID: number | null = null
  private _isMounted = false

  static defaultProps: DefaultProps = {
    fields: [],
    noResultsMessage: 'global.search_no_results_found',
    orderBy: '',
    perPage: 5,
    query: ''
  }

  state: State<T> = {
    data: [],
    isLoading: true,
    types: {},
    selectedRow: null,
    modal: false
  }

  async fetchData(): Promise<IPaginatedResponse<T>> {
    const { action, apiFields, fields, orderBy, perPage, query, queryValue } = this.props
    const fieldsForQuery = apiFields ? apiFields.toString() : fields.map(f => (typeof f === 'string' ? f : f.key)).toString()
    const params = {
      fields: fieldsForQuery,
      orderBy,
      page: 1,
      perPage,
      search: `${query}${queryValue}`
    }

    return action(params) as any
  }

  refresh = async () => {
    const { Data, Structure } = await this.fetchData()

    if (this._isMounted) {
      this.setState({
        data: Data,
        isLoading: false,
        types: Structure
      })
    }
  }

  async componentDidMount() {
    this._isMounted = true

    await this.refresh()

    if (this.props.refreshInterval && this.props.refreshInterval > 0) {
      this.timerID = window.setInterval(() => {
        this.refresh()
      }, this.props.refreshInterval)
    }
  }

  componentDidUpdate(prevProps: Props<T>) {
    if (
      prevProps.orderBy !== this.props.orderBy ||
      prevProps.perPage !== this.props.perPage ||
      prevProps.query !== this.props.query ||
      prevProps.queryValue !== this.props.queryValue
    ) {
      this.refresh()
    }
  }

  async componentWillUnmount() {
    this._isMounted = false

    this.timerID && window.clearInterval(this.timerID)
  }

  showDeleteModal = (row: T) => () => {
    this.setState({
      selectedRow: row,
      modal: true
    })
  }

  closeModal = () => {
    this.setState({
      modal: false
    })
  }

  handleRemove = async (dryRun: boolean, reason?: string) => {
    const { deleteOptions } = this.props
    const { selectedRow } = this.state

    if (!deleteOptions || !selectedRow) return

    const data = await deleteOptions.onDelete(selectedRow, dryRun, reason, this.props)

    if (!dryRun) {
      await this.refresh()
    }

    return data
  }

  render() {
    const { data, isLoading, types } = this.state
    const { button, fields, noResultsMessage, deleteOptions, scrollable, scrollHeight } = this.props

    const shouldRenderButton = Boolean(button)
    const shouldRenderDelete = Boolean(deleteOptions)
    const renderActionColumn = shouldRenderButton || shouldRenderDelete

    const filteredFields = fields
      .map(f => (typeof f === 'string' ? { key: f } : f))
      .filter((field: FieldWithOptionalCustomRender) => !/uuid/i.test(field.key))

    if (!data || (!isLoading && data.length === 0)) {
      return <NoResultMessage message={noResultsMessage} />
    }

    if (isLoading) {
      return <Loader loading />
    }

    return (
      <>
        <Table sx={{ marginTop: 1, maxHeight: scrollable ? scrollHeight : 'unset' }} stickyHeader={scrollable}>
          <TableHead>
            <TableRow data-testid='table-row-head'>
              {filteredFields.map(field => (
                <HeaderCell key={field.key} field={field.key} />
              ))}
              {renderActionColumn && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody data-testid='table-row-body'>
            {data.map((row, key) => (
              // This `wordBreak` was added for https://lkqeurope.atlassian.net/browse/GMS-6462
              // so if this visually breaks anything refer to that story and ask the PO.
              <TableRow key={key} sx={{ wordBreak: 'break-all' }}>
                {filteredFields.map(field => {
                  let Component = field.Component
                  let content = row as any
                  if (!Component) {
                    const v = getColumnValue(row, field.key, types)
                    // Cast to React.ComponentType as an escape hatch.
                    Component = v.component as React.ComponentType<any> | undefined
                    content = v.content
                  }

                  return <TableCell key={field.key}>{Component ? <Component content={content} /> : content}</TableCell>
                })}
                {renderActionColumn && (
                  <TableCell rowSpan={1} align='right' sx={{ overflowWrap: 'initial', maxWidth: 'initial' }}>
                    <ButtonGroup>
                      {button && button(row)}
                      {shouldRenderDelete && <DeleteButton onClick={this.showDeleteModal(row)} />}
                    </ButtonGroup>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {this.state.modal && deleteOptions && (
          <DeleteModalContainer
            data-testid='delete-modal'
            showModal={this.state.modal}
            closeModal={this.closeModal}
            item={i18next.t(`error.delete.delete_item_${deleteOptions.translationKey}`)}
            handleRemove={this.handleRemove}
            skipDryRun={deleteOptions.skipDryRun}
            deleteReasons={deleteOptions.deleteReasons}
            isPortal={this.props.isPortal}
            mapApiResponseToMessage={deleteOptions.mapApiResponseToMessage}
          />
        )}
      </>
    )
  }
}

const EnhancedKnockWidget = enhance(KnockWidget)

const KWidget = <TEntity extends {}>(
  props: Partial<DefaultProps> & Omit<OwnProps<TEntity>, 'ref'> & { forwardedRef?: React.RefObject<any> }
) => {
  const Comp = EnhancedKnockWidget as React.ComponentType<Partial<DefaultProps> & OwnProps<TEntity>>

  return <Comp {...(props as Partial<DefaultProps> & OwnProps<TEntity>)} ref={props.forwardedRef} />
}

export default KWidget
